<template>
  <b-modal
    v-model="show"
    centered
    size="md"
    data-test="file-overview"
    @hidden="$emit('hidden')"
  >
    <template #modal-title>
      <h3 class="font-weight-bold">Project File</h3>
    </template>

    <template v-if="file">
      <b-row>
        <b-col>
          <table class="file-info">
            <tr>
              <td>File Name:</td>
              <td data-test="overview-name">{{ file.name }}</td>
            </tr>
            <tr>
              <td>Type:</td>
              <td data-test="overview-type">{{ typeLabel }}</td>
            </tr>
            <tr>
              <td>Size:</td>
              <td data-test="overview-size">{{ sizeLabel }}</td>
            </tr>
            <tr>
              <td>Expiry Date:</td>
              <td data-test="overview-expiry">
                <span data-test="overview-expiry-date">{{ expiryDate }}</span>
                <!-- Same pills as the file list. An archived file is no longer
                     being kept current, so no alarm. -->
                <b-badge
                  v-if="level === 'expired'"
                  v-b-tooltip.hover="expiryText"
                  pill
                  variant="danger"
                  class="file-status-badge"
                  data-test="overview-expired"
                  >Expired</b-badge
                >
                <b-badge
                  v-else-if="level === 'expiring'"
                  v-b-tooltip.hover="expiryText"
                  pill
                  variant="warning"
                  class="file-status-badge file-status-badge--soon"
                  data-test="overview-expiring"
                  >Expiring soon</b-badge
                >
              </td>
            </tr>
            <tr>
              <td>Tags:</td>
              <td data-test="overview-tags">
                <template v-if="file.tags && file.tags.length">
                  <button
                    v-for="tag in file.tags"
                    :key="tag"
                    type="button"
                    class="file-tag"
                    :title="`Show files tagged ${tag}`"
                    data-test="overview-tag"
                    @click="$emit('filter-tag', tag)"
                  >
                    {{ tag }}
                  </button>
                </template>
                <template v-else>-</template>
              </td>
            </tr>
            <tr>
              <td>Uploaded By:</td>
              <td data-test="overview-uploader">{{ uploaderName }}</td>
            </tr>
            <tr>
              <td>Date Uploaded:</td>
              <td data-test="overview-uploaded">{{ uploadedText }}</td>
            </tr>
            <tr v-if="isArchived">
              <td>Status:</td>
              <td data-test="overview-status">Archived</td>
            </tr>
            <tr v-if="isArchived">
              <td>Date Archived:</td>
              <td data-test="overview-archived">{{ archivedText }}</td>
            </tr>
          </table>
        </b-col>
      </b-row>

      <!-- Same layout as a tradie's file: a heading, a 100px thumbnail that
           opens the full-size file in a preview dialog, and the note under it. -->
      <b-row class="attachments-row my-4">
        <b-col class="file-attachments-col">
          <p class="font-weight-bold opacity-100">Uploaded attachment*</p>
          <div class="file-attachments-scroll mb-2">
            <div
              v-if="preview.state === 'loading'"
              class="file-img file-img--tile"
              data-test="preview-loading"
            >
              <b-spinner small />
            </div>

            <a
              v-else-if="canOpen"
              :href="preview.url"
              target="_blank"
              rel="noopener"
              class="file-attachment-link"
              data-test="preview-link"
              @click.prevent="previewOpen = true"
            >
              <img
                v-if="preview.kind === 'image'"
                :src="preview.url"
                :alt="`Preview of ${file.name}`"
                class="file-img"
                data-test="preview-image"
                @error="onPreviewError"
              />
              <img
                v-else
                :src="tileIcon"
                :alt="`${typeLabel} file`"
                class="file-img file-img--icon"
                data-test="preview-icon"
              />
            </a>

            <!-- CSV / Excel / Word can't be shown by the browser itself, but the
                 dialog reads them through the API and shows a table or a
                 simplified document. -->
            <button
              v-else-if="canPreviewContent"
              type="button"
              class="file-attachment-link file-attachment-button"
              :title="`Preview ${file.name}`"
              data-test="preview-content"
              @click="previewOpen = true"
            >
              <img
                :src="tileIcon"
                :alt="`${typeLabel} file`"
                class="file-img file-img--icon"
                data-test="preview-icon"
              />
            </button>

            <button
              v-else-if="canDownload"
              type="button"
              class="file-attachment-link file-attachment-button"
              :title="`Download ${file.name}`"
              data-test="preview-download"
              @click="$emit('download', file)"
            >
              <img
                :src="tileIcon"
                :alt="`${typeLabel} file`"
                class="file-img file-img--icon"
                data-test="preview-icon"
              />
            </button>

            <img
              v-else
              :src="tileIcon"
              :alt="`${typeLabel} file`"
              class="file-img file-img--icon"
              data-test="preview-icon"
            />
          </div>
          <span data-test="preview-note">{{ previewNote }}</span>
        </b-col>
      </b-row>
    </template>

    <template #modal-footer>
      <div
        v-if="file && (canDelete || canEdit || canDownload)"
        class="d-flex w-100 justify-content-between view-file-footer"
        style="gap: 12px"
        data-test="overview-actions"
      >
        <b-button
          v-if="canDelete"
          variant="danger"
          data-test="overview-delete"
          @click="$emit('delete', file)"
        >
          DELETE<span class="d-none d-sm-inline"> FILE</span>
        </b-button>
        <!-- Archive / Unarchive are reversible → outline, no confirmation. -->
        <b-button
          v-if="canEdit && !isArchived"
          variant="outline-primary"
          :disabled="busy"
          data-test="overview-archive"
          @click="$emit('archive', file)"
        >
          ARCHIVE
        </b-button>
        <b-button
          v-if="canEdit && isArchived"
          variant="outline-primary"
          :disabled="busy"
          data-test="overview-restore"
          @click="$emit('restore', file)"
        >
          UNARCHIVE
        </b-button>
        <b-button
          v-if="canDownload"
          :variant="canEdit ? 'outline-primary' : 'primary'"
          :class="{ 'ml-auto': !canDelete && !canEdit }"
          :disabled="downloading"
          data-test="overview-download"
          @click="$emit('download', file)"
        >
          DOWNLOAD
        </b-button>
        <b-button
          v-if="canEdit"
          variant="primary"
          data-test="overview-edit"
          @click="$emit('edit', file)"
        >
          EDIT<span class="d-none d-sm-inline"> FILE</span>
        </b-button>
      </div>
      <div v-else></div>
    </template>

    <FilePreviewModal
      v-if="file && (canOpen || canPreviewContent)"
      :visible="previewOpen"
      :url="canOpen ? preview.url : ''"
      :content-url="canPreviewContent ? contentUrl : ''"
      :mime-type="file.mime_type"
      :name="file.file_name || file.name"
      :allow-download="canDownload"
      :busy="downloading"
      custom-download
      @download="$emit('download', file)"
      @close="previewOpen = false"
    />
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex'
import dayjs from 'dayjs'
import { nonImageDocIcon } from '@/utils/fileTypeIcons'
import FilePreviewModal from '@/components/modals/FilePreviewModal.vue'
import {
  expiryDescription,
  fileExtension,
  fileTypeLabel,
  formatBytes,
} from '@/utils/projectFiles'

/**
 * The file overview: a centered modal opened by clicking a file, laid out like
 * viewing a tradie's file (ViewFileModal) — bold title, "Label:" rows, a
 * thumbnail that opens the full file in a preview dialog, and a footer of equal
 * uppercase buttons. It does no writes itself: it emits, and the panel runs the
 * (already permission-checked) flows.
 */
export default {
  name: 'ProjectFileOverview',

  components: { FilePreviewModal },

  props: {
    file: { type: Object, default: null },
    projectId: { type: [Number, String], required: true },
    visible: { type: Boolean, default: false },
    // What this user may do — decided by the panel (permissions + read-only).
    canDownload: { type: Boolean, default: false },
    canEdit: { type: Boolean, default: false },
    canDelete: { type: Boolean, default: false },
    // Something is in flight for this file.
    downloading: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
  },

  data() {
    return {
      // idle | loading | ready | none (no browser preview for the type) | error
      preview: { state: 'idle', kind: null, url: null },
      // The full-size preview dialog, opened from the thumbnail.
      previewOpen: false,
    }
  },

  computed: {
    // v-model for the modal: Esc, the ×, or a click outside all ask to close.
    show: {
      get() {
        return this.visible
      },
      set(open) {
        if (!open) this.$emit('close')
      },
    },

    isArchived() {
      return !!(this.file && this.file.archived_at)
    },

    // 'expired' | 'expiring' | null. Archived files never raise an alarm.
    level() {
      if (!this.file || this.isArchived) return null
      if (this.file.expiry_status === 'expired') return 'expired'
      if (this.file.expiry_status === 'expiring') return 'expiring'
      return null
    },

    typeLabel() {
      return this.file
        ? fileTypeLabel(this.file.mime_type, this.file.file_name)
        : ''
    },

    sizeLabel() {
      return this.file ? formatBytes(this.file.size) : ''
    },

    expiryDate() {
      const at = this.file && this.file.expires_at
      return at ? dayjs(at).format('DD/MM/YYYY') : '-'
    },

    // The exact wording, for the pill's tooltip.
    expiryText() {
      return expiryDescription(this.file, { archived: this.isArchived })
    },

    uploaderName() {
      const by = this.file && this.file.uploaded_by
      return by && by.full_name ? by.full_name : '-'
    },

    uploadedText() {
      const at = this.file && this.file.created_at
      return at ? dayjs(at).format('DD/MM/YYYY h:mm A') : '-'
    },

    archivedText() {
      const at = this.file && this.file.archived_at
      return at ? dayjs(at).format('DD/MM/YYYY') : '-'
    },

    // The browser can show this file (an image or a PDF) at a signed URL.
    canOpen() {
      return this.preview.state === 'ready' && !!this.preview.url
    },

    // CSV / Excel / Word: the browser can't show these itself, but the dialog
    // reads them through the API and shows a table / document. Judged on the
    // stored file's extension (the API does the same before it will hand the
    // contents over).
    canPreviewContent() {
      return (
        !!this.file &&
        ['csv', 'xlsx', 'docx'].includes(fileExtension(this.file.file_name))
      )
    },

    // Where the dialog reads the file's contents from.
    contentUrl() {
      return this.file
        ? `projects/${this.projectId}/files/${this.file.id}/content`
        : ''
    },

    // Bundled icon for anything that isn't a real image thumbnail.
    tileIcon() {
      if (!this.file) return require('@/assets/icons/file.svg')
      const ext = fileExtension(this.file.file_name)
      // First, and on the extension alone: a .docx is a zip, so its mime type is
      // unreliable — nonImageDocIcon() below would take a zip for an Excel file.
      if (ext === 'docx') return require('@/assets/icons/docx-file.svg')
      const doc = nonImageDocIcon(this.file)
      if (doc) return doc
      if (ext === 'pdf' || (this.file.mime_type || '').indexOf('pdf') !== -1) {
        return require('@/assets/icons/pdf-file.svg')
      }
      return require('@/assets/icons/file.svg')
    },

    previewNote() {
      if (this.preview.state === 'loading') return 'Loading preview…'
      if (this.canPreviewContent || this.canOpen) {
        return '*click on the file to preview it.'
      }
      if (this.canDownload) return '*click on the file to download it.'
      return this.preview.state === 'error'
        ? '*preview unavailable right now.'
        : '*no preview for this type of file.'
    },

    // Fetch a preview only while the modal is open, and again when it moves to
    // a different file or that file's document is replaced (document_id changes
    // then, but not on a rename or tag edit), so the thumbnail never shows the
    // old document.
    previewKey() {
      if (!this.visible || !this.file) return null
      return `${this.file.id}|${this.file.document_id || ''}`
    },
  },

  watch: {
    previewKey: {
      immediate: true,
      handler(key) {
        this.previewOpen = false
        if (key === null || key === undefined) {
          this.previewRequest = (this.previewRequest || 0) + 1
          this.preview = { state: 'idle', kind: null, url: null }
          return
        }
        this.loadPreview(this.file.id)
      },
    },
  },

  methods: {
    ...mapActions('projectFile', ['getPreview']),

    async loadPreview(id) {
      // A slow answer for a file we've since moved off must not overwrite the
      // current one.
      const request = (this.previewRequest = (this.previewRequest || 0) + 1)
      this.preview = { state: 'loading', kind: null, url: null }

      try {
        const data = await this.getPreview({ projectId: this.projectId, id })
        if (request !== this.previewRequest) return
        this.preview =
          data && data.url && data.kind
            ? { state: 'ready', kind: data.kind, url: data.url }
            : { state: 'none', kind: null, url: null }
      } catch (error) {
        if (request !== this.previewRequest) return
        this.preview = { state: 'error', kind: null, url: null }
      }
    },

    // The signed link expired or the image is corrupt: fall back to the icon.
    onPreviewError() {
      this.preview = { state: 'error', kind: null, url: null }
    },
  },
}
</script>

<style lang="scss" scoped>
// Copied from ViewFileModal so a project file looks like a tradie's file.
.file-attachments-col {
  min-width: 0;
}

/* Up to 4 buttons can render here (Delete, Archive, Download, Edit). The global
 * .btn has wide (32px) side padding that pushes them off the row at typical
 * modal widths, so always shrink padding + font and let the buttons share the
 * width evenly, keeping them on one row rather than wrapping. */
.view-file-footer {
  flex-wrap: wrap;
  row-gap: 8px;
  gap: 8px !important;

  .btn {
    flex: 1 1 0;
    min-width: 0;
    padding-left: 0.4rem;
    padding-right: 0.4rem;
    font-size: 0.8rem;
    white-space: nowrap;
  }
}

table.file-info {
  tr td:first-child {
    padding-right: 16px;
    font-weight: bold;
    white-space: nowrap;
    vertical-align: top;
  }

  tr td {
    padding-bottom: 8px;
  }
}

.file-attachments-scroll {
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 4px;
}

.file-attachment-link {
  display: inline-block;
}

.file-attachment-button {
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.file-img {
  width: 100px;
  height: 100px;
  background-color: lightgray;
  opacity: 0.8;
  object-fit: cover;
}

.file-img:hover {
  opacity: 1;
}

// Document icons sit inside the same grey box; contain, don't crop.
.file-img--icon {
  object-fit: contain;
  padding: 12px;
}

.file-img--tile {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

// Same pills as the file list (and the tradie files list).
.file-status-badge {
  margin-left: 0.5rem;
  font-weight: 600;
}

.file-status-badge--soon {
  background-color: #fff3cd !important;
  color: #856404 !important;
  border: 1px solid #ffc107;
}

// Tag chip — same as the list.
.file-tag {
  display: inline-block;
  margin: 0 4px 4px 0;
  padding: 0 0.6rem;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  background: #f3f4f6;
  color: #374151;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.7;
  cursor: pointer;

  &:hover {
    border-color: #2c66d5;
  }
}

/* Phones: strip the modal down, same as ViewFileModal. */
@media (max-width: 575.98px) {
  /deep/ .modal-dialog {
    margin: 0.5rem;
  }

  /deep/ .modal-header {
    padding: 0.75rem 1rem;
  }

  /deep/ .modal-body {
    padding: 0.75rem 1rem;
  }

  /deep/ .modal-footer {
    padding: 0.5rem 1rem;
  }

  h3.font-weight-bold {
    font-size: 1.15rem;
    margin-bottom: 0;
  }

  /* Four buttons across (Delete, Archive, Download, Edit) is one more than a
   * tradie's file ever has, so tighten them a little more on a phone. */
  .view-file-footer .btn {
    font-size: 0.7rem;
    padding-left: 0.2rem;
    padding-right: 0.2rem;
  }

  table.file-info tr td {
    padding-bottom: 4px;
    font-size: 0.85rem;
  }

  .attachments-row {
    margin-top: 0.6rem !important;
    margin-bottom: 0.25rem !important;
  }

  .file-attachments-col {
    font-size: 0.78rem;
  }

  .file-attachments-col p {
    margin-bottom: 0.35rem !important;
    font-size: 0.85rem;
  }

  .file-img {
    width: 76px;
    height: 76px;
  }
}
</style>
